import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Trophy, Medal, Award } from 'lucide-react';
import { DriverLeaderboardEntryDto } from '@/types';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';

interface DriverLeaderboardProps {
  leaderboard?: DriverLeaderboardEntryDto[];
  currentDriverId: string;
  isLoading?: boolean;
  onDriverClick?: (driverId: string, driverName: string) => void;
}

// Medal icons for top 3
const RankIcon = ({ rank }: { rank: number }) => {
  switch (rank) {
    case 1:
      return <Trophy className="h-5 w-5 text-podium-gold" />;
    case 2:
      return <Medal className="h-5 w-5 text-podium-silver" />;
    case 3:
      return <Award className="h-5 w-5 text-podium-bronze" />;
    default:
      return (
        <span className="w-5 h-5 flex items-center justify-center text-sm text-gray-500 font-medium">
          {rank}
        </span>
      );
  }
};

export function DriverLeaderboard({
  leaderboard = [],
  currentDriverId,
  isLoading,
  onDriverClick,
}: DriverLeaderboardProps) {

  if (isLoading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base" style={{ color: '#000000' }}>
            <Trophy className="h-5 w-5 text-podium-gold" />
            Driver Leaderboard
          </CardTitle>
        </CardHeader>
        <CardContent className="h-[250px] flex items-center justify-center">
          <div className="animate-pulse text-gray-500">
            Loading leaderboard...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (leaderboard.length === 0) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base" style={{ color: '#000000' }}>
            <Trophy className="h-5 w-5 text-podium-gold" />
            Driver Leaderboard
          </CardTitle>
        </CardHeader>
        <CardContent className="h-[250px] flex items-center justify-center">
          <p className="text-gray-500 text-sm">No data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base" style={{ color: '#000000' }}>
            <Trophy className="h-5 w-5 text-podium-gold" />
            Driver Leaderboard
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <ScrollArea className="h-[250px] pr-4">
          <div className="space-y-2">
            {leaderboard.map((entry) => {
              const isCurrentDriver = entry.driverId === currentDriverId;
              const isTopThree = entry.rank <= 3;

              return (
                <div
                  key={entry.driverId}
                  onClick={() => onDriverClick?.(entry.driverId, entry.driverName)}
                  className={cn(
                    'flex items-center justify-between p-3 rounded-lg transition-colors cursor-pointer',
                    isCurrentDriver
                      ? 'bg-green-50 border-2 border-green-200'
                      : isTopThree
                        ? 'bg-gray-50 hover:bg-gray-100'
                        : 'hover:bg-gray-50'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <RankIcon rank={entry.rank} />
                    <div className="flex flex-col">
                      <span
                        className="font-medium text-sm truncate max-w-[150px]"
                        style={{ color: isCurrentDriver ? '#15803d' : '#111827' }}
                      >
                        {entry.driverName}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className="text-sm font-semibold tabular-nums"
                      style={{ color: isCurrentDriver ? '#15803d' : '#111827' }}
                    >
                      {entry.totalCompletedCalls}
                    </span>
                    <span className="text-xs" style={{ color: '#6b7280' }}>calls</span>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
